import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../../i18n";
import { GoogleBusinessLogo } from "../../platformCatalog";
import {
  fetchAvailableGoogleLocations,
  selectGoogleLocations,
} from "../../googleBusinessApi";

/**
 * Plan-aware Google location picker.
 *
 * Shows every location associated with the connected Google email and lets
 * the user choose which one(s) to connect:
 *   - trial / enterprise  -> any number ("Alle auswählen" available)
 *   - basic / premium     -> exactly one (radio behavior, switching allowed)
 */
const GoogleLocationPickerModal = ({ open, onClose, onApplied, refreshOnOpen = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const firstInputRef = useRef(null);

  const limit = data?.location_limit ?? null; // null = unlimited
  const singleMode = limit === 1;
  const overLimit = Boolean(data?.over_limit);
  const locations = useMemo(() => data?.locations || [], [data]);

  const load = useCallback(
    async ({ refresh } = { refresh: false }) => {
      setLoading(true);
      setLoadError("");
      try {
        const payload = await fetchAvailableGoogleLocations({ refresh });
        setData(payload);
        const connected = (payload.locations || [])
          .filter((l) => l.is_connected)
          .map((l) => l.id);
        const limitOne = (payload.location_limit ?? null) === 1;
        if (payload.over_limit || (limitOne && connected.length > 1)) {
          // Over the plan limit: force an explicit choice rather than
          // showing several "checked" radios.
          setSelected(new Set());
        } else if (connected.length > 0) {
          setSelected(new Set(connected));
        } else if (limitOne && (payload.locations || []).length === 1) {
          // Single location + single-slot plan: preselect the obvious choice.
          setSelected(new Set([payload.locations[0].id]));
        } else {
          setSelected(new Set());
        }
      } catch (err) {
        setLoadError(
          err?.data?.detail || err?.message || t("platforms.locationPicker.loadError")
        );
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (!open) return;
    setSaveError("");
    load({ refresh: refreshOnOpen });
  }, [open, load, refreshOnOpen]);

  // Move keyboard focus into the list once it is ready.
  useEffect(() => {
    if (open && !loading && !loadError && locations.length > 0) {
      firstInputRef.current?.focus?.();
    }
  }, [open, loading, loadError, locations.length]);

  // Escape closes (unless a save is running).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  const toggle = (id) => {
    setSaveError("");
    setSelected((prev) => {
      if (singleMode) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = locations.length > 0 && selected.size === locations.length;
  const toggleAll = () => {
    setSaveError("");
    setSelected(allSelected ? new Set() : new Set(locations.map((l) => l.id)));
  };

  const handleConfirm = async () => {
    if (selected.size === 0 || saving) return;
    setSaving(true);
    setSaveError("");
    try {
      const payload = await selectGoogleLocations([...selected]);
      onApplied?.(payload);
    } catch (err) {
      if (err?.data?.code === "plan_limit") {
        setSaveError(t("platforms.limit.message"));
      } else {
        setSaveError(
          err?.data?.detail || err?.message || t("platforms.locationPicker.saveError")
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const planHintKey =
    {
      trial: "platforms.locationPicker.planHintTrial",
      basic: "platforms.locationPicker.planHintBasic",
      premium: "platforms.locationPicker.planHintPremium",
      enterprise: "platforms.locationPicker.planHintEnterprise",
    }[data?.plan] || "platforms.locationPicker.planHintTrial";

  const confirmLabel = overLimit
    ? t("platforms.limit.keepThis")
    : selected.size > 1
      ? t("platforms.locationPicker.confirmMany", { count: selected.size })
      : t("platforms.locationPicker.confirmOne");

  // Basic/Premium with more locations than their plan allows: make the
  // Enterprise path obvious and hand them straight to support.
  const showEnterpriseUpsell = singleMode && locations.length > 1;
  const requestEnterprise = () => {
    onClose?.();
    navigate("/dashboard/client/support", {
      state: {
        presetCategory: "support",
        presetSubject: t("platforms.locationPicker.enterpriseRequestSubject"),
        presetMessage: t("platforms.locationPicker.enterpriseRequestMessage", {
          count: locations.length,
        }),
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="google-location-picker-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-[#0f172a]/45 backdrop-blur-[2px]"
        aria-label={t("common.close")}
        onClick={() => !saving && onClose?.()}
      />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-[16px] border border-[#e8ecf0] bg-white shadow-[0_20px_50px_rgba(15,23,42,0.18)]">
        {/* header */}
        <div className="border-b border-[#f1f5f9] px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] border border-[#e8ecf0] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
              <GoogleBusinessLogo className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h2
                id="google-location-picker-title"
                className="text-[18px] font-bold leading-snug text-[#0f172a]"
              >
                {overLimit
                  ? t("platforms.limit.overLimitTitle")
                  : t("platforms.locationPicker.title")}
              </h2>
              <p className="mt-1 text-[13px] leading-relaxed text-[#64748b]">
                {t("platforms.locationPicker.subtitle", {
                  email: data?.provider_email || "…",
                })}
              </p>
            </div>
          </div>
          {overLimit ? (
            <div className="mt-3 flex items-start gap-3 rounded-[10px] border border-amber-200 bg-[#fffbeb] px-3.5 py-3">
              <span
                aria-hidden
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white"
              >
                !
              </span>
              <p className="text-[12.5px] leading-relaxed text-amber-900">
                {t("platforms.limit.overLimitPickerHint")}
              </p>
            </div>
          ) : (
            <p className="mt-3 rounded-[10px] bg-[#f0fafa] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-[#0d4d4d]">
              {t(planHintKey)}
            </p>
          )}
          {showEnterpriseUpsell ? (
            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-[#0f172a]/10 bg-[#0f172a] px-3.5 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-bold text-white">
                  {t("platforms.locationPicker.upsellTitle")}
                </p>
                <p className="mt-0.5 text-[12px] leading-relaxed text-white/70">
                  {t("platforms.locationPicker.upsellBody")}
                </p>
              </div>
              <button
                type="button"
                onClick={requestEnterprise}
                className="shrink-0 rounded-full bg-white px-4 py-2 text-[12.5px] font-bold text-[#0f172a] shadow-sm transition hover:bg-[#f0fafa] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("platforms.locationPicker.upsellCta")} →
              </button>
            </div>
          ) : null}
        </div>

        {/* body */}
        <div className="min-h-[160px] flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <ul className="space-y-3" aria-hidden>
              {[1, 2, 3].map((i) => (
                <li
                  key={i}
                  className="h-[72px] animate-pulse rounded-[12px] border border-[#eef2f5] bg-[#f8fafc]"
                />
              ))}
            </ul>
          ) : loadError ? (
            <div className="rounded-[12px] border border-amber-300 bg-amber-50 px-4 py-4 text-center">
              <p className="text-[13px] text-amber-900">{loadError}</p>
              <button
                type="button"
                onClick={() => load({ refresh: true })}
                className="mt-3 rounded-full border border-amber-400 px-4 py-1.5 text-[12.5px] font-semibold text-amber-900 transition hover:bg-amber-100"
              >
                {t("platforms.locationPicker.retry")}
              </button>
            </div>
          ) : locations.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-[14px] font-semibold text-[#0f172a]">
                {t("platforms.locationPicker.empty")}
              </p>
              <p className="mx-auto mt-1 max-w-sm text-[12.5px] leading-relaxed text-[#64748b]">
                {t("platforms.locationPicker.emptyHint")}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#9ca3af]">
                  {locations.length === 1
                    ? t("platforms.locationPicker.oneAvailable")
                    : t("platforms.locationPicker.countAvailable", {
                        count: locations.length,
                      })}
                </span>
                {!singleMode && locations.length > 1 ? (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="rounded-full px-3 py-1 text-[12px] font-semibold text-[#006666] transition hover:bg-[#f0fafa]"
                  >
                    {allSelected
                      ? t("platforms.locationPicker.deselectAll")
                      : t("platforms.locationPicker.selectAll")}
                  </button>
                ) : null}
              </div>

              <ul
                className="space-y-2.5"
                role={singleMode ? "radiogroup" : "group"}
                aria-label={t("platforms.locationPicker.title")}
              >
                {locations.map((loc, index) => {
                  const isSelected = selected.has(loc.id);
                  const reviewCount = loc.platform_total_review_count;
                  const rating = loc.platform_average_rating;
                  return (
                    <li key={loc.id}>
                      <label
                        className={[
                          "group relative flex cursor-pointer items-center gap-3.5 rounded-[12px] border-2 px-4 py-3.5 transition-all duration-150",
                          isSelected
                            ? "border-[#006666] bg-[#f0fafa] shadow-[0_2px_10px_rgba(0,102,102,0.10)]"
                            : "border-[#e8ecf0] bg-white hover:border-[#c7d2da] hover:bg-[#fafcfd]",
                        ].join(" ")}
                      >
                        <input
                          ref={index === 0 ? firstInputRef : undefined}
                          type={singleMode ? "radio" : "checkbox"}
                          name="google-location-choice"
                          className="peer sr-only"
                          checked={isSelected}
                          onChange={() => toggle(loc.id)}
                        />
                        {/* selection indicator */}
                        <span
                          aria-hidden
                          className={[
                            "flex h-[22px] w-[22px] shrink-0 items-center justify-center border-2 transition-all duration-150",
                            singleMode ? "rounded-full" : "rounded-[7px]",
                            isSelected
                              ? "border-[#006666] bg-[#006666]"
                              : "border-[#cbd5e1] bg-white group-hover:border-[#94a3b8]",
                          ].join(" ")}
                        >
                          {isSelected ? (
                            singleMode ? (
                              <span className="h-2 w-2 rounded-full bg-white" />
                            ) : (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M5 12.5l4.5 4.5L19 7.5"
                                  stroke="#fff"
                                  strokeWidth="3.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )
                          ) : null}
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="truncate text-[14.5px] font-bold text-[#0f172a]">
                              {loc.name}
                            </span>
                            {loc.is_connected ? (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-200">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                                {t("platforms.locationPicker.connectedBadge")}
                              </span>
                            ) : null}
                          </span>
                          {reviewCount || rating ? (
                            <span className="mt-0.5 block text-[12px] text-[#64748b]">
                              {rating ? `★ ${Number(rating).toLocaleString("de-DE", { maximumFractionDigits: 1 })}` : null}
                              {rating && reviewCount ? " · " : null}
                              {reviewCount
                                ? t("platforms.locationPicker.reviewsMeta", {
                                    count: reviewCount,
                                  })
                                : null}
                            </span>
                          ) : null}
                        </span>

                        {/* focus ring for keyboard users */}
                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-0 rounded-[12px] ring-[#006666] ring-offset-2 peer-focus-visible:ring-2"
                        />
                      </label>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* footer */}
        <div className="border-t border-[#f1f5f9] bg-[#fafcfd] px-6 py-4">
          {saveError ? (
            <p className="mb-3 rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-800">
              {saveError}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[12.5px] text-[#64748b]" aria-live="polite">
              {selected.size > 0
                ? t("platforms.locationPicker.selectedCount", { count: selected.size })
                : t("platforms.locationPicker.noneSelected")}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => !saving && onClose?.()}
                disabled={saving}
                className="rounded-full px-4 py-2.5 text-[13.5px] font-medium text-[#64748b] transition hover:text-[#0f172a] disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={selected.size === 0 || saving || loading || Boolean(loadError)}
                className="rounded-full bg-[#006666] px-6 py-2.5 text-[13.5px] font-bold text-white shadow-[0_4px_14px_rgba(0,102,102,0.3)] transition hover:bg-[#00504a] disabled:opacity-50 disabled:shadow-none"
              >
                {saving ? t("platforms.locationPicker.saving") : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleLocationPickerModal;
